// Contact form validation mirroring ContactFieldsSchema in packages/contracts.
import type { Contact, ContactFields } from '@bcio-voice/contracts';
import { parsePhoneInput, phoneParseMessage } from '../../lib/format.ts';

export interface ContactDraft {
  display_name: string;
  company_name: string;
  phones: { input: string; label: string }[];
  emails: string[];
}

export type ContactErrors = Partial<Record<string, string>>;

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function emptyDraft(): ContactDraft {
  return { display_name: '', company_name: '', phones: [{ input: '', label: 'Mobile' }], emails: [] };
}

export function draftFromContact(contact: Contact): ContactDraft {
  return {
    display_name: contact.display_name,
    company_name: contact.company_name ?? '',
    phones: contact.phones.length ? contact.phones.map(p => ({ input: p.e164, label: p.label })) : [{ input: '', label: 'Mobile' }],
    emails: [...contact.emails],
  };
}

export function validateContact(draft: ContactDraft): { ok: true; fields: ContactFields } | { ok: false; errors: ContactErrors } {
  const errors: ContactErrors = {};
  const name = draft.display_name.trim();
  if (!name) errors.display_name = 'Enter a name.';
  else if (name.length > 200) errors.display_name = 'Use 200 characters or fewer.';
  const company = draft.company_name.trim();
  if (company.length > 200) errors.company_name = 'Use 200 characters or fewer.';

  const phones: ContactFields['phones'] = [];
  const seen = new Set<string>();
  const filledPhones = draft.phones.filter(p => p.input.trim() || p.label.trim());
  if (filledPhones.length > 20) errors.phones = 'A contact can have up to 20 numbers.';
  draft.phones.forEach((p, i) => {
    if (!p.input.trim() && !p.label.trim()) return;
    const parsed = parsePhoneInput(p.input);
    if (!parsed.ok) {
      errors[`phones.${i}`] = phoneParseMessage(parsed);
      return;
    }
    const label = p.label.trim() || 'Phone';
    if (label.length > 40) {
      errors[`phones.${i}.label`] = 'Use 40 characters or fewer.';
      return;
    }
    if (seen.has(parsed.e164)) {
      errors[`phones.${i}`] = 'This number is already listed.';
      return;
    }
    seen.add(parsed.e164);
    phones.push({ e164: parsed.e164, label });
  });

  const emails: string[] = [];
  const filledEmails = draft.emails.map(e => e.trim()).filter(Boolean);
  if (filledEmails.length > 20) errors.emails = 'A contact can have up to 20 email addresses.';
  draft.emails.forEach((raw, i) => {
    const e = raw.trim();
    if (!e) return;
    if (e.length > 254 || !EMAIL.test(e)) errors[`emails.${i}`] = 'Enter an email address like name@example.com.';
    else emails.push(e);
  });

  if (Object.keys(errors).length) return { ok: false, errors };
  return { ok: true, fields: { display_name: name, company_name: company || null, phones, emails } };
}
